/**
 * Breakable supports — issue #41, Phase 5.
 *
 * WHAT WAS MISSING. `Flag.STATIC` means "does not fall", and everything in the
 * world that carries it carries it unconditionally: worldgen deposits, altars,
 * ability caches, the prologue's authored corridor. So the world can be
 * *destroyed* but it cannot *collapse*. Dig the pillar out from under a ledge
 * and the ledge does not notice, because nothing in the game has ever had the
 * property "this is holding something up".
 *
 * A **support group** is that property, and nothing more: a set of cells that
 * are STATIC *conditionally*, plus the anchor cells the condition reads. When
 * the number of surviving anchors drops below the group's threshold, the group
 * loses STATIC and stops being exempt from gravity.
 *
 * THE FALL IS NOT HERE, DELIBERATELY. `cellsim` already steps loose matter and
 * `particles` already handles the spray. This file clears a flag; everything
 * after that is systems we already shipped, and a `swap` or an `emit` in here
 * would be a second falling-sand implementation competing with the first.
 * `tests/supports.test.ts` asserts that at the source level rather than trusting
 * it, because "I will not write the fall" is exactly the kind of boundary that
 * erodes one convenience at a time.
 *
 * DETERMINISTIC WORK QUOTA, NOT A CLOCK. docs/01 §9: the same seed must produce
 * the same world on a fast machine and a slow one, so no simulation system may
 * schedule off `performance.now()`. The quota here is *derived* from the
 * structural caps rather than tuned:
 *
 *     MAX_GROUPS × (1 residency probe + MAX_ANCHORS peeks)  =  64 × 9  =  576
 *     MAX_GROUP_CELLS release allowance                     =        =  256
 *                                                             ---------------
 *     maxWorkPerTick                                                    832
 *
 * The release allowance is exactly one whole group, which is what makes the
 * quota safe without a slack term: a group that fails always finishes releasing
 * on the tick it failed, so there is no resumption point to over-run and no
 * state in which a load hangs half-static in mid-air. Several groups failing on
 * the same tick queue up and drain one per tick, in id order, which is
 * order-independent and therefore replay-safe.
 *
 * 832 units against `cellsim`'s 24000 is 3.5%, and the steady-state cost with
 * nothing failing is the 512 peeks the plan budgeted. This system does not need
 * an LOD tier and does not have one.
 */

import { CellWorld } from './world';
import { Flag, type MatId } from './materials';

/**
 * Registry cap.
 *
 * Not arbitrary: `CellWorld.updateActive` evicts past 256 resident chunks, and
 * `supportSites.ts` draws a site in one chunk in `SITE_PERIOD` (4). A fully
 * resident world therefore *wants* 64 groups, and asking for more would mean
 * the cap was silently doing placement design.
 */
export const MAX_GROUPS = 64;

/**
 * Anchors sampled per group.
 *
 * Eight, spread along the span rather than clustered: the failure this guards
 * against is a plank that has burned through in the middle while both ends are
 * intact, which a two-anchor group reports as sound.
 */
export const MAX_ANCHORS = 8;

/**
 * Cells one group may hold up.
 *
 * 256 is a 32×8 slab — the largest load `supportSites.ts` authors — and it is
 * also the release allowance above, which is the same number for a reason. Grow
 * this and the quota grows with it; grow it without the quota and a collapse
 * spreads its release over several ticks.
 */
export const MAX_GROUP_CELLS = 256;

/**
 * What generation hands over. Coordinates are flat `[x, y, x, y, …]` pairs in
 * world cells — a pair array rather than objects because registration happens
 * inside chunk generation, which is on the path of every chunk the player walks
 * into and allocates nothing per cell today.
 */
export interface SupportGroupSpec {
  /**
   * Registry key. `supportSites.ts` writes `"<cx>,<cy>:<situation>"`, so a
   * chunk that is evicted and regenerated re-registers over its own entry
   * instead of accumulating duplicates.
   */
  id: string;
  /** Player-facing line for the interaction ticker (docs/06 §4). */
  label: string;
  /** An anchor survives while its cell still holds this material. */
  anchorMat: MatId;
  /** Surviving anchors needed to keep holding. 1..anchor count. */
  threshold: number;
  anchors: readonly number[];
  cells: readonly number[];
}

/**
 * A group's lifecycle.
 *
 * `ARMING` exists for a reason that is not obvious and cost an afternoon to
 * find. The generator cannot write the load's STATIC itself: `CellWorld.
 * getChunk` recounts a freshly generated chunk and, finding no *mobile,
 * non-STATIC* matter in it, concludes there is nothing to simulate — it clears
 * the dirty rects and puts the chunk straight to sleep. A niche stamped as
 * STATIC at generation is therefore a 1150 °C lava pocket that never conducts
 * a single degree into the plank holding it up. So the generator leaves the
 * load loose, the chunk wakes normally, and the group takes ownership of the
 * flag on its first step — which runs before `cellSim` in the same tick, so
 * nothing ever gets a chance to fall.
 *
 * The group owning STATIC for the whole of its life is the better invariant
 * anyway: "these cells are static because something is holding them" is stated
 * in one place instead of split between a generator and a release path.
 */
const ARMING = 0;
const HOLDING = 1;
const RELEASING = 2;
/** Spent or evicted — dropped from the registry at the end of the tick. */
const DONE = 3;

interface Group {
  id: string;
  label: string;
  anchorMat: MatId;
  threshold: number;
  anchors: Int32Array;
  cells: Int32Array;
  state: number;
  /** Index into `cells` of the next pair to write, while arming or releasing. */
  cursor: number;
}

function toPairs(v: readonly number[]): Int32Array {
  const out = new Int32Array(v.length);
  for (let i = 0; i < v.length; i++) out[i] = v[i] | 0;
  return out;
}

export class Supports {
  private readonly world: CellWorld;

  /** Live groups, kept sorted by id so iteration never depends on load order. */
  private readonly groups: Group[] = [];

  /**
   * The §9 quota, derived above. Read by `tests/supports.test.ts` the way
   * `tests/sim.test.ts` reads every other system's.
   */
  readonly maxWorkPerTick = MAX_GROUPS * (1 + MAX_ANCHORS) + MAX_GROUP_CELLS;

  /** Units spent on the last `step`. Telemetry, and the thing under the gate. */
  workThisTick = 0;

  /**
   * Registrations refused because the registry was full.
   *
   * Reported rather than thrown: a world that generates a 65th site is a
   * placement-density question, not a crash. It is asserted in the suite so the
   * number cannot climb unnoticed.
   */
  droppedGroups = 0;

  /**
   * Fired once, on the tick a group gives way. The game wires this to the
   * interaction ticker — docs/06 §4, communication beats simulation: a ceiling
   * that collapses silently is indistinguishable from a bug.
   */
  onFail: ((label: string, x: number, y: number) => void) | null = null;

  constructor(world: CellWorld) {
    this.world = world;
  }

  get groupCount(): number {
    return this.groups.length;
  }

  /** Registered ids, in registry order. Used by the determinism fixtures. */
  groupIds(): string[] {
    const out: string[] = [];
    for (let i = 0; i < this.groups.length; i++) out.push(this.groups[i].id);
    return out;
  }

  clear(): void {
    this.groups.length = 0;
    this.droppedGroups = 0;
  }

  /**
   * Register at generation time. Idempotent in the id: re-registering replaces,
   * which is what a regenerated chunk needs — the generator has just rewritten
   * the cells as STATIC again, so the group must come back intact rather than
   * remembering that it already fell.
   */
  register(spec: SupportGroupSpec): void {
    const nAnchors = spec.anchors.length >> 1;
    const nCells = spec.cells.length >> 1;
    if (spec.anchors.length % 2 !== 0 || nAnchors < 1 || nAnchors > MAX_ANCHORS) {
      throw new Error(`support ${spec.id}: ${nAnchors} anchors, cap is ${MAX_ANCHORS}`);
    }
    if (spec.cells.length % 2 !== 0 || nCells < 1 || nCells > MAX_GROUP_CELLS) {
      throw new Error(`support ${spec.id}: ${nCells} cells, cap is ${MAX_GROUP_CELLS}`);
    }
    if (spec.threshold < 1 || spec.threshold > nAnchors) {
      throw new Error(`support ${spec.id}: threshold ${spec.threshold} outside 1..${nAnchors}`);
    }

    const g: Group = {
      id: spec.id,
      label: spec.label,
      anchorMat: spec.anchorMat,
      threshold: spec.threshold,
      anchors: toPairs(spec.anchors),
      cells: toPairs(spec.cells),
      state: ARMING,
      cursor: 0,
    };

    const at = this.seek(spec.id);
    if (at >= 0) {
      this.groups[at] = g;
      return;
    }
    if (this.groups.length >= MAX_GROUPS) {
      // Refuse the newcomer rather than evicting a live group: eviction would
      // make which supports exist depend on the order chunks happened to load.
      this.droppedGroups++;
      return;
    }
    this.groups.splice(~at, 0, g);
  }

  /** Binary search. Returns the index, or `~insertionPoint` when absent. */
  private seek(id: string): number {
    let lo = 0;
    let hi = this.groups.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const c = this.groups[mid].id;
      if (c === id) return mid;
      if (c < id) lo = mid + 1;
      else hi = mid - 1;
    }
    return ~lo;
  }

  /**
   * One tick.
   *
   * Detection first, then release, so a group that fails on this tick loses
   * STATIC on this tick — the load must never be observable hanging over a plank
   * that is already gone. The two phases are separately bounded and together sum
   * to exactly `maxWorkPerTick`, so neither can starve the other.
   */
  step(): void {
    let work = 0;
    const groups = this.groups;

    // ---- phase 1: which supports are still standing.
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      if (g.state === RELEASING || g.state === DONE) continue;

      // Residency probe. `peek` reports ungenerated space as VOID, so a group
      // whose chunk has been evicted would read as having lost every anchor and
      // would drop its load on an empty room — and then be gone when the player
      // walked back in. An absent chunk is not a collapse; it is a group that
      // no longer applies, and the generator will re-register it on return.
      work++;
      if (this.world.peekChunkAt(g.anchors[0], g.anchors[1]) === null) {
        g.state = DONE;
        continue;
      }
      if (g.state === ARMING) continue;

      let alive = 0;
      const a = g.anchors;
      for (let k = 0; k < a.length; k += 2) {
        work++;
        if (this.world.peek(a[k], a[k + 1]) !== g.anchorMat) continue;
        // A burning anchor is a failing anchor, not a surviving one. Waiting for
        // the wood to be *consumed* costs `burnDuration` — 260 ticks for WOOD —
        // and that delay is not a detail: the plank-over-lava fixture measured
        // its load fully solidified to obsidian before the plank finished
        // burning, so the canonical chain ended with a rigid slab that could
        // never fall. "It caught, then it gave way" is also the sequence the
        // talk describes, and it is legible in a way "it caught, and four
        // seconds later it gave way" is not.
        if ((this.world.getFlags(a[k], a[k + 1]) & Flag.BURNING) !== 0) continue;
        alive++;
      }
      if (alive < g.threshold) {
        g.state = RELEASING;
        g.cursor = 0;
        if (this.onFail !== null) this.onFail(g.label, a[0], a[1]);
      }
    }

    // ---- phase 2: write the flag, up to one whole group's worth per tick.
    let budget = MAX_GROUP_CELLS;
    for (let i = 0; i < groups.length && budget > 0; i++) {
      const g = groups[i];
      if (g.state !== ARMING && g.state !== RELEASING) continue;
      const c = g.cells;
      const arming = g.state === ARMING;
      let k = g.cursor;
      while (k < c.length && budget > 0) {
        // The entire mechanism, both directions. Everything the player sees
        // after a release belongs to `cellsim` and `particles`.
        if (arming) this.world.addFlag(c[k], c[k + 1], Flag.STATIC);
        else this.world.clearFlag(c[k], c[k + 1], Flag.STATIC);
        k += 2;
        budget--;
        work++;
      }
      if (k < c.length) g.cursor = k;
      else {
        g.cursor = 0;
        g.state = arming ? HOLDING : DONE;
      }
    }

    // ---- compact: spent and evicted groups both leave the registry.
    let w = 0;
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      if (g.state === DONE) continue;
      groups[w++] = g;
    }
    groups.length = w;

    this.workThisTick = work;
  }
}
